import type { Account, ChainType } from '@polaris/core/models'
import { atom } from 'jotai'
import type { AggregatedWalletOption } from '@/src/hooks/wallet/use-wallet-options'

interface ChangingAccountWalletInfo {
  walletOption: AggregatedWalletOption
  connectedAccounts: Account[]
}

/** Whether the select wallets dialog is open */
export const selectWalletsDialogOpenAtom = atom<boolean>(false)

/** The chain type for the select wallets dialog */
export const selectWalletsDialogChainTypeAtom = atom<ChainType | undefined>(
  undefined,
)

/** Optional watched address being connected via the dialog */
export const selectWalletsDialogWatchAddressAtom = atom<string | undefined>(
  undefined,
)

/** Information about the wallet being changed during account switching */
export const changingAccountWalletInfoAtom = atom<
  ChangingAccountWalletInfo | undefined
>(undefined)

/** Combined state atom for the select wallets dialog */
export const selectWalletsDialogAtom = atom(
  (get) => ({
    open: get(selectWalletsDialogOpenAtom),
    chainType: get(selectWalletsDialogChainTypeAtom),
    watchAddress: get(selectWalletsDialogWatchAddressAtom),
  }),
  (
    _get,
    set,
    update: { open: boolean; chainType?: ChainType; watchAddress?: string },
  ) => {
    set(selectWalletsDialogOpenAtom, update.open)
    if (update.chainType) {
      set(selectWalletsDialogChainTypeAtom, update.chainType)
    } else {
      set(selectWalletsDialogChainTypeAtom, undefined)
    }
    if (update.watchAddress) {
      set(selectWalletsDialogWatchAddressAtom, update.watchAddress)
    } else {
      set(selectWalletsDialogWatchAddressAtom, undefined)
    }
  },
)

/** Helper functions for common dialog actions */
export const openSelectWalletsDialog = (
  chainType?: ChainType,
  watchAddress?: string,
) => ({
  open: true,
  chainType,
  watchAddress,
})

export const closeSelectWalletsDialog = () => ({
  open: false,
  chainType: undefined,
  watchAddress: undefined,
})

/** Helper functions for changing account wallet actions */
export const setChangingAccountWallet = (
  walletOption: AggregatedWalletOption,
  connectedAccounts: Account[],
): ChangingAccountWalletInfo => ({
  walletOption,
  connectedAccounts,
})

export const clearChangingAccountWallet = (): undefined => undefined
